'use client'
'use memo'

import type { ComponentProps } from 'react'
import type { Address } from 'viem'

import { Button, cn, CopyButton, Text, type TextProps } from '@repo/ui'
import { ArrowDiagonalUp } from '@repo/ui/icons'
import { abbreviateAddress, formatTokenBalance } from '@repo/utils'
import { useBalance, useBytecode, useEnsName, useTransactionCount } from 'wagmi'

import { AddressAvatar } from './AddressAvatar'
import {
  EntityHoverCard,
  EntityHoverCardContent,
  EntityHoverCardDivider,
  EntityHoverCardHeader,
  EntityHoverCardRow,
  EntityHoverCardRows,
  EntityHoverCardTrigger
} from './EntityHoverCard'
import { EtherscanLink } from './EtherscanLink'

interface AddressDetailedPreviewProps extends Omit<ComponentProps<'div'>, 'children'> {
  chainId: number
  address: Address
  name?: string
  textSize?: TextProps['size']
  /**
   * Renders the bare identity — no hover card or tab stop — for a cell inside a container
   * that already owns the interaction (e.g. an expandable table row).
   */
  interactive?: boolean
}

function useResolvedEnsName(address: Address) {
  const { data: ensName, isLoading } = useEnsName({ address, chainId: 1 })
  return { ensName: ensName ?? null, isLoading }
}

export function AddressDetailedPreviewContent({
  chainId,
  address
}: {
  chainId: number
  address: Address
}) {
  const { ensName } = useResolvedEnsName(address)
  const { data: balance, isLoading: balanceLoading } = useBalance({ address, chainId })
  const { data: bytecode, isLoading: bytecodeLoading } = useBytecode({ address, chainId })
  const { data: txCount, isLoading: txCountLoading } = useTransactionCount({ address, chainId })

  const isContract = !!bytecode && bytecode.length > 2
  const abbreviated = abbreviateAddress(address)

  return (
    <>
      <EntityHoverCardHeader
        avatar={<AddressAvatar address={address} size={24} />}
        title={ensName ?? abbreviated}
        subtitle={ensName ? abbreviated : undefined}
        actions={
          <>
            <CopyButton
              value={address}
              size="iconSm"
              variant="ghost"
              className="text-muted-foreground"
              successMessage="Copied address to clipboard"
              label={`Copy address ${abbreviated}`}
            />
            <Button
              size="iconSm"
              variant="ghost"
              asChild
              className="text-muted-foreground"
              aria-label={`View ${abbreviated} on Etherscan`}
            >
              <EtherscanLink address={address} chainId={chainId}>
                <ArrowDiagonalUp className="text-muted-foreground size-5" />
              </EtherscanLink>
            </Button>
          </>
        }
      />

      <EntityHoverCardDivider />

      <EntityHoverCardRows aria-label="Address details">
        <EntityHoverCardRow label="Type" loading={bytecodeLoading} skeletonClassName="w-16">
          <Text size="xs">
            {isContract ? (
              'Contract'
            ) : (
              <abbr title="Externally Owned Account" className="no-underline">
                EOA
              </abbr>
            )}
          </Text>
        </EntityHoverCardRow>

        <EntityHoverCardRow label="Balance" loading={balanceLoading} skeletonClassName="w-20">
          <Text size="xs">
            {balance
              ? formatTokenBalance({
                  value: balance.value,
                  decimals: balance.decimals,
                  symbol: balance.symbol
                })
              : '0'}
          </Text>
        </EntityHoverCardRow>

        <EntityHoverCardRow label="Nonce" loading={txCountLoading} skeletonClassName="w-12">
          <Text size="xs">{txCount?.toLocaleString() ?? '0'}</Text>
        </EntityHoverCardRow>
      </EntityHoverCardRows>
    </>
  )
}

export function AddressDetailedPreview({
  chainId,
  address,
  name,
  textSize = 'xs',
  className,
  interactive = true,
  ...props
}: AddressDetailedPreviewProps) {
  const { ensName } = useResolvedEnsName(address)
  const displayName = name ?? ensName ?? abbreviateAddress(address)

  const identity = (
    <>
      <span aria-hidden="true">
        <AddressAvatar address={address} size={16} />
      </span>
      <Text tag="span" size={textSize} className="relative top-px truncate">
        {displayName}
      </Text>
    </>
  )

  if (!interactive) {
    return (
      <div className={cn('inline-flex w-fit items-center gap-1.5', className)} {...props}>
        {identity}
      </div>
    )
  }

  return (
    <div className={cn('inline-flex w-fit items-center gap-2', className)} {...props}>
      <EntityHoverCard>
        <EntityHoverCardTrigger asChild>
          <div
            role="button"
            tabIndex={0}
            aria-label={`Address details for ${displayName}`}
            className="hover:bg-muted data-[state=open]:bg-muted inline-flex cursor-default items-center gap-1.5 rounded-md px-2 py-1 transition-colors"
          >
            {identity}
          </div>
        </EntityHoverCardTrigger>
        <EntityHoverCardContent>
          <AddressDetailedPreviewContent chainId={chainId} address={address} />
        </EntityHoverCardContent>
      </EntityHoverCard>
    </div>
  )
}
